import React, { useCallback, useEffect, useState } from 'react'
import { reqGet, TOneItemCallback } from 'HttpRequests'
import PasswordInput from 'components/inputs/PasswordInput'
import BlueButton from 'components/inputs/BlueButton'
import EmptyLink from 'components/EmptyLink'
import { reqChangePasswordWithCode, reqSendRecoveryCode } from 'auth/LoginRequests'
import NumberInput from 'components/inputs/NumberInput'
import MySpinner from 'components/MySpinner'
import PhoneInput from 'components/inputs/PhoneInput'
import CaptchaInput from 'components/inputs/CaptchaInput'

interface IRecoveryFormProps {
  loginPhone: string
  onChangeLoginPhone: TOneItemCallback<string>
  onLogin: (login: string, password: string, isRemember: boolean, captcha: string, captchaHash: string) => void
  onCancel: VoidFunction
  onSetMessage: TOneItemCallback<string>
}

const RecoveryForm: React.FC<IRecoveryFormProps> = ({
  loginPhone,
  onChangeLoginPhone,
  onLogin,
  onCancel,
  onSetMessage,
}) => {
  const [firstPassword, setFirstPassword] = useState<string>('')
  const [secondPassword, setSecondPassword] = useState<string>('')
  const [firstPasswordError, setFirstPasswordError] = useState<string>('')
  const [secondPasswordError, setSecondPasswordError] = useState<string>('')
  const [isFirstValid, setIsFirstValid] = useState(true)
  const [isSecondValid, setIsSecondValid] = useState(true)
  const [code, setCode] = useState<number | undefined>(undefined)
  const [isCodeSending, setIsCodeSending] = useState(false)
  const [isCodeSend, setIsCodeSend] = useState(false)
  const [captcha, setCaptcha] = useState('')
  const [captchaImg, setCaptchaImg] = useState('')
  const [captchaHash, setCaptchaHash] = useState('')

  const handleChangeCode = (value: number | '') => {
    if (value === '') {
      setCode(undefined)
    } else {
      setCode(value)
    }
  }

  const handleChangeFirstPassword = useCallback((value, isValid) => {
    setIsFirstValid(isValid)
    setFirstPassword(value)
  }, [])

  const handleSecondChangePassword = useCallback((value, isValid) => {
    setIsSecondValid(isValid)
    setSecondPassword(value)
  }, [])

  useEffect(() => {
    if (!firstPassword) setFirstPasswordError('Введите пароль')
    else setFirstPasswordError('')

    if (!secondPassword) setSecondPasswordError('Повторите пароль')

    if (firstPassword.length > 0) {
      if (firstPassword !== secondPassword) {
        setSecondPasswordError('Пароли должны совпадать')
      } else {
        setSecondPasswordError('')
      }
    }
  }, [firstPassword, secondPassword])

  useEffect(() => {
    reqGet("/api/captcha/", (resp) => {
      setCaptchaImg(resp.img)
      setCaptchaHash(resp.hash)
    })
  }, [])

  const handleSendCode = useCallback(() => {
    // console.log(loginPhone)

    onSetMessage('')
    setIsCodeSend(false)
    setIsCodeSending(true)
    reqSendRecoveryCode(loginPhone, captcha, captchaHash, (response) => {
      setIsCodeSending(false)
      if (response.status === 'INVALID_PHONE_NUMBER') {
        onSetMessage('Пользователь с таким номером не зарегистрирован')
      } else if (response.status === 'CAPTCHA_ERROR') {
        onSetMessage('Попробуйте еще раз')
        reqGet("/api/captcha/", (resp) => {
          setCaptchaImg(resp.img)
          setCaptchaHash(resp.hash)
          setCaptcha('')
        })
      } else if (response.status !== 'OK') {
        onSetMessage('Невозможно отправить СМС-код подтверждения')
      } else {
        setIsCodeSend(true)
      }
    })
  }, [loginPhone, onSetMessage, captcha, captchaHash])

  const handleRecover = useCallback(() => {
    if (
      loginPhone.length === 0 ||
      !isFirstValid ||
      !isSecondValid ||
      firstPassword.length === 0 ||
      secondPassword.length === 0 ||
      firstPassword !== secondPassword ||
      captcha.length < 6
    ) {
      onSetMessage('Заполните поля')
    } else {
      reqChangePasswordWithCode(
        loginPhone,
        code || -1,
        firstPassword,
        secondPassword,
        (response) => {
          const status = response.status
          if (status === 'OK') {
            onSetMessage('')
            onLogin(loginPhone, firstPassword, true, captcha, captchaHash)
          } else if (status === 'BAD_CODE') {
            onSetMessage('Неверный код или его срок действия истек')
          } else if (status === 'INVALID_PHONE_NUMBER') {
            onSetMessage('Неверно набран номер')
          } else if (status === 'INVALID_PASSWORD') {
            onSetMessage('Введите другой пароль')
          } else if (status === 'NOT_SAME_PASSWORDS') {
            onSetMessage('Пароли должны совпадать')
          } else if (status === 'SAME_AS_OLD') {
            onSetMessage('Новый и старый пароли не должны совпадать')
          } else {
            onSetMessage('Неизвестная ошибка.')
          }
        }
      )
    }
  }, [
    loginPhone,
    code,
    firstPassword,
    secondPassword,
    isFirstValid,
    isSecondValid,
    onLogin,
    onSetMessage,
    captcha,
    captchaHash
  ])

  return (
    <div>
      <p>Восстановление доступа</p>
      <PhoneInput
        isRequired
        defaultValue={loginPhone}
        onChange={() => {}}
        onEntered={onChangeLoginPhone}
        style={{ width: '100%' }}
      />
      <div className='m-2'></div>

      {isCodeSending ? (
        <>
          <MySpinner />
        </>
      ) : !isCodeSend ? (
        <>
          <div className='m-2'></div>
          <input type="hidden" value={captchaHash} />
            <img src={"data:image/jpeg;base64, " + captchaImg} alt="Captcha" />
            <CaptchaInput value={captcha} onChange={setCaptcha} /><div className='m-2'></div>
          <div style={{ marginBottom: '1rem' }}>
            <BlueButton onClick={handleSendCode} style={{ width: '100%' }}>
              Отправить код восстановления
            </BlueButton>
          </div>
        </>
      ) : (
        <>
          <p>Введите код подтверждения</p>
          <NumberInput defaultValue={code} onChange={handleChangeCode} />
          <div className='m-2'></div>

          <PasswordInput
            required
            value={firstPassword}
            errorMessage={firstPasswordError}
            placeholder='новый пароль'
            onChange={handleChangeFirstPassword}
            maxLength={20}
            style={{ width: '100%' }}
          />

          <div className='m-2'></div>
          <PasswordInput
            required
            value={secondPassword}
            errorMessage={secondPasswordError}
            placeholder='подтвердите пароль'
            onChange={handleSecondChangePassword}
            onEntered={handleRecover}
            maxLength={20}
            minLength={8}
            style={{ width: '100%' }}
          />
          

          <div className='m-2'></div>
          <div style={{ marginBottom: '1rem' }}>
            <BlueButton onClick={handleRecover} style={{ width: '100%' }}>
              Сменить пароль
            </BlueButton>
          </div>
        </>
      )}
      <EmptyLink onClick={onCancel}>Отмена</EmptyLink>
    </div>
  )
}
export default RecoveryForm
